<template>
  <div
    ref="root"
    class="ak-dropdown"
    :class="rootClasses"
  >
    <div
      v-if="!inline"
      ref="trigger"
      role="button"
      class="ak-dropdown__trigger"
      aria-haspopup="true"
      v-click-outside="clickedOutside"
      @click.prevent="showMenu"
      @mouseleave="toggleHover(true)"
      @mouseover="toggleHover()"
    >
      <slot name="trigger" />
    </div>
    <transition :name="animationType">
      <div
        v-show="(!disabled && isActive) || inline"
        ref="dropdownMenu"
        v-trap-focus="trapFocus"
        class="ak-dropdown__menu"
        :class="menuClasses"
        :aria-hidden="!isActive"
        @click.stop
      >
        <div
          class="ak-dropdown__body"
          :class="bodyClasses"
          :role="ariaRoleMenu"
        >
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'
import trapFocus from '../../directives/trapFocus'
import config from '../../utils/config'
import clickOutside from '../../directives/clickOutside'

const DEFAULT_CLOSE_OPTIONS = ['escape', 'outside']

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-dropdown>
   *   <ak-dropdown-item>One</ak-dropdown-item>
   *   <ak-dropdown-item>Two</ak-dropdown-item>
   * </ak-dropdown>
   */
  name: 'AkDropdown',

  /**
   * @type {Object}
   * @description A collection of directives that this component is
   * dependant on.
   */
  directives: {
    clickOutside,
    trapFocus,
  },

  provide() {
    return {
      akDropdown: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Mixed}
     * @description The value when selected
     */
    modelValue: {
      type: [String, Number, Boolean, Object, Array, Function],
      default: null,
    },

    /**
     * @type {Boolean}
     * @description Disables dropdown
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Disables dropdown
     */
    hover: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Dropdown content (items) are shown inline, trigger
     * is removed
     */
    inline: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Dropdown menu is a dark theme
     */
    dark: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Optional, position of the dropdown relative to the
     * trigger
     */
    positionX: {
      default: 'left',
      type: String,
      validator(value) {
        return ['right', 'left'].indexOf(value) > -1
      },
    },

    /**
     * @type {String}
     * @description Optional, position of the dropdown relative to the
     * trigger
     */
    positionY: {
      default: 'bottom',
      type: String,
      validator(value) {
        return ['bottom', 'top'].indexOf(value) > -1
      },
    },

    /**
     * @type {String}
     * @description Optional, position of the dropdown relative to the
     * trigger
     */
    size: {
      default: null,
      type: String,
      validator(value) {
        return ['auto', 'big', 'large', 'huge'].indexOf(value) > -1
      },
    },

    /**
     * @type {String}
     * @description Role attribute to be passed to list container for better
     * accessibility. Use menu only in situations where your dropdown is
     * related to navigation menus.
     */
    ariaRole: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description Custom animation (transition name)
     */
    animation: {
      type: String,
      default: 'scale-down',
    },

    /**
     * @type {Boolean}
     * @description Allows multiple selections
     */
    multiple: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Trap focus inside the dropdown.
     */
    trapFocus: {
      type: Boolean,
      default: config.defaultTrapFocus,
    },

    /**
     * @type {Boolean}
     * @description Close dropdown when content is clicked
     */
    closeOnClick: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Array|Boolean}
     * @description Can close dropdown by pressing escape or by clicking outside
     * @example
     * <ak-dropdown :can-close=false></ak-dropdown>
     * <ak-dropdown :can-close="[escape, outside]"></ak-dropdown>
     */
    canClose: {
      type: [Array, Boolean],
      default: true,
    },

    /**
     * @type {Number}
     * @description Sets columns in the dropdown body
     */
    columns: {
      type: Number,
      default: undefined,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue', 'change'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const isActive = ref(false)
    const isDropdown = ref(true)
    const root = ref(null)
    const trigger = ref(null)
    const dropdownMenu = ref(null)

    /**
     * @type {Object}
     * @description This will be the selected value of the dropdown, this will
     * return the modelValue, and will update the v-model when a new value is
     * set.
     */
    const selected = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to apply to the dropdown
     * element
     */
    const rootClasses = computed(() => {
      return {
        'ak-dropdown--dark': props.dark,
        'is-disabled': props.disabled,
        'ak-dropdown--inline': props.inline,
        'ak-dropdown--active': isActive.value || props.inline,
      }
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to apply to the dropdown
     * element
     */
    const menuClasses = computed(() => {
      const classes = [
        `ak-dropdown__menu--${props.size}`,
        `ak-dropdown__menu--${props.positionX}`,
        `ak-dropdown__menu--${props.positionY}`,
      ]
      return classes
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to apply to the dropdown
     * body
     */
    const bodyClasses = computed(() => {
      const classes = []
      if (props.columns) {
        classes.push(`ak-dropdown__body--${props.columns}-columns`)
      }
      return classes
    })

    /**
     * @returns {Array}
     * @description Returns the cancel options for the dropdown
     */
    const cancelOptions = computed(() => {
      return typeof props.canClose === 'boolean'
        ? props.canClose
          ? DEFAULT_CLOSE_OPTIONS
          : /* istanbul ignore next */ []
        : /* istanbul ignore next */ props.canClose
    })

    /**
     * @returns {String|Null}
     * @description Returns the aria-role property for the dropdown menu
     */
    const ariaRoleMenu = computed(() => {
      return props.ariaRole === 'menu' || props.ariaRole === 'list'
        ? props.ariaRole
        : null
    })

    /**
     * @returns {String}
     * @description Returns an animation depending on positionY
     */
    const animationType = computed(() => {
      const animationName = 'scale'
      const animationDirectionY = props.positionY === 'top' ? 'bottom' : 'top'
      const animationDirectionX = props.positionX === 'left' ? 'right' : 'left'
      return `${animationName}-${animationDirectionY}-${animationDirectionX}`
    })

    /**
     * @description Opens the menu
     */
     const showMenu = () => {
      isActive.value = true
    }

    /**
     * @description Toggle dropdown on hover if it's enabled.
     * @param close
     */
    const toggleHover = (close) => {
      if (!props.hover || props.disabled) return
      if (close) {
        isActive.value = false
      } else {
        isActive.value = true
      }
    }

    /**
     * @param {Mixed}
     * @description Click listener from DropdownItem.
     * 1. Set new selected item.
     * 2. Emit input event to update the user v-model.
     * 3. Close the dropdown.
     */
    const selectItem = (value) => {
      if (props.multiple) {
        /* istanbul ignore else */
        if (selected.value) {
          const index = selected.value.indexOf(value)
          if (index === -1) {
            selected.value.push(value)
          } else {
            selected.value.splice(index, 1)
          }
        } else {
          selected.value = [value]
        }
      } else {
        if (selected.value !== value) {
          selected.value = value
        }
      }

      if (!props.multiple) {
        isActive.value = !props.closeOnClick
      }
    }

    /**
     * @description Close dropdown if clicked outside.
     */
    const clickedOutside = () => {
      isActive.value = false
    }

    /**
     * @param {Event} event
     * @description Keypress event that is bound to the document
     */
    const keyPress = (event) => {
      if (isActive.value && event.keyCode === 27) {
        if (cancelOptions.value.indexOf('escape') < 0) return
        isActive.value = false
      }
    }

    watch(
      () => isActive.value,
      (newVal) => {
        emit('change', newVal)
      }
    )

    onMounted(() => {
      /* istanbul ignore else */
      if (typeof window !== 'undefined') {
        document.addEventListener('keyup', keyPress)
      }
    })

    onBeforeUnmount(() => {
      /* istanbul ignore else */
      if (typeof window !== 'undefined') {
        document.removeEventListener('keyup', keyPress)
      }
    })

    return {
      rootClasses,
      menuClasses,
      bodyClasses,
      cancelOptions,
      animationType,
      selected,
      isActive,
      isDropdown,
      ariaRoleMenu,
      root,
      trigger,
      dropdownMenu,
      showMenu,
      toggleHover,
      selectItem,
      clickedOutside,
      keyPress,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
$columns: ((2), (3));
.ak-dropdown {
  $self: &;
  position: relative;
  display: inline-flex;
  align-items: center;

  &.is-disabled {
    &,
    &:hover,
    &:focus {
      opacity: 0.5;
    }
    button,
    .ak-btn {
      cursor: not-allowed;
    }
  }

  &__trigger {
    height: 100%;
    display: inline-flex;
    align-items: center;
  }

  &__menu {
    position: absolute;
    width: 180px;
    max-width: 90vw;
    z-index: 10;

    // Size modifiers
    &--auto {
      width: auto;
    }
    &--big {
      width: 250px;
    }
    &--large {
      width: 400px;
    }
    &--huge {
      width: 550px;
    }

    // Position modifiers
    &--top {
      bottom: 100%;
      padding-bottom: $spacing-tiny;
    }
    &--bottom {
      top: 100%;
      padding-top: $spacing-tiny;
    }
    &--left {
      left: 0;
    }
    &--right {
      right: 0;
    }
  }

  &__body {
    padding-top: $spacing-small;
    padding-bottom: $spacing-small;
    background-color: #fff;
    box-shadow: $base-shadow-large;
    border-radius: $base-border-radius;
    @each $value in $columns {
      &--#{$value}-columns {
        column-count: $value;
        column-gap: 0;
        > * {
          display: inline-block;
          width: 100%;
        }
      }
    }
  }

  &--dark {
    #{$self}__body {
      background-color: $color-grey-dark;
      color: #fff;
    }
  }
}
</style>
